import { useNavigate } from 'react-router-dom';
import type { FC, ReactNode } from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { PageBody, PageHeading, PageSurface } from '../../../design-system/components';
import styles from './Legal.module.scss';

const Clause: FC<{ number: string; title: string; children: ReactNode }> = ({ number, title, children }) => (
  <section className={styles.clause}>
    <h2 className={styles.clause__heading}>
      <span className={styles.clause__number}>{number}</span>
      {title}
    </h2>
    <div className={styles.prose}>{children}</div>
  </section>
);

const Legal: FC = () => {
  const navigate = useNavigate();

  const handleNavItemClick = (item: string) => {
    switch (item) {
      case 'Model Exploration':
        navigate('/model-exploration');
        break;
      case 'Tools':
        navigate('/tools');
        break;
      case 'Learning':
        navigate('/learning');
        break;
      case 'Contact':
        navigate('/contact');
        break;
      default:
        break;
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header pageTitle="Legal" onNavItemClick={handleNavItemClick} />

      <PageSurface>
        <PageHeading title="Legal information" />

        <PageBody>
          <div className={styles.document}>
            <div className={styles.prose}>
              <p>
                Welcome to the Cerebellum Hub, dissemination Hub of the Neurocomputation unit of the Department of
                Brain and Behavioral Sciences, Università degli studi di Pavia.
              </p>
              <p>
                Unless otherwise stated, the Hub content includes everything you see, hear or otherwise receive from,
                and anything you download or otherwise use from, including but not limited to microcircuit, data,
                models, software, images, illustrations, designs, video, icons, photographs, text, sounds, music,
                code, services, workflows and tools("Content").
              </p>
              <p>
                By accessing, browsing, or using the Hub and its Content, you agree to the following terms and
                conditions of use and to any future revision. We may change, suspend, discontinue, limit or remove any
                part of the Content or change the Terms at any time, without prior notice or liability. Your continued
                use of the Hub and its Content following any changes is an agreement by you to the change in Terms.
                Nothing in these Terms transfers to you any right, title or interest in any Content, including any
                intellectual property or content of third-party websites included on the Hub.
              </p>
            </div>

            <section className={styles.part}>
              <h2 className={styles.part__title}>Note Legali / Terms of Use</h2>
              <div className={styles.prose}>
                <p>
                  Le presenti note legali sono state redatte conformemente alle Linee Guida per i siti web del 2011
                  del Ministro della Pubblica Amministrazione e della semplificazione{' '}
                  <a href="http://www.funzionepubblica.gov.it/" target="_blank" rel="noopener noreferrer">
                    http://www.funzionepubblica.gov.it/
                  </a>
                </p>
              </div>
            </section>

            <Clause number="1." title="Copyright">
              <p>
                Tutti i contenuti (in via esemplificativa e non esaustiva si citano immagini, testo, audio, files,
                contenuti del sito, organizzazione del materiale, codice di script, grafica, podcast, video) presenti
                in questo sito dell’Università degli Studi di Pavia sono pubblicati nel rispetto dei diritti di
                proprietà intellettuale dei legittimi titolari. I diritti sugli oggetti e sui contenuti del sito
                restano di proprietà dei titolari degli stessi e sono protetti ai sensi della vigente normativa in
                materia di Diritto d’Autore. La riproduzione dei contenuti del sito potrà avvenire con il previo
                consenso dei titolari dei diritti, solo per uso personale e per scopi non commerciali, salvo i
                contenuti eventualmente rilasciati sotto licenza creative commons. In caso di riproduzione dei
                contenuti del sito è necessario citarne la fonte. Per qualsiasi informazione o dubbio sui contenuti
                pubblicati e sulle relative licenze di utilizzo, è possibile inviare mail a{' '}
                <a href="mailto:neurocomp_dbbs@unipv.it">neurocomp_dbbs@unipv.it</a>
              </p>
            </Clause>

            <Clause number="2." title="Utilizzo del sito Cerebhub.eu">
              <p>
                Per fornire informazioni il più possibile aggiornate e corrette, si chiede la collaborazione dei
                visitatori per la segnalazione di eventuali imprecisioni o disfunzioni, inviando una mail
                all’indirizzo <a href="mailto:neurocomp_dbbs@unipv.it">neurocomp_dbbs@unipv.it</a>.
              </p>
              <p>La redazione fornirà un riscontro nel più breve tempo possibile.</p>
              <p>
                L’Università degli Studi di Pavia non potrà essere ritenuta in alcun modo responsabile dei danni di
                qualsiasi natura causati direttamente o indirettamente dall’accesso e fruizione del sito e dei relativi
                servizi oppure dall’utilizzo degli strumenti interattivi.
              </p>
              <p>
                L’Università degli Studi di Pavia non rilascia alcun tipo di garanzia, esplicita o implicita, riguardo
                contenuti e le informazioni presenti sul sito, ivi compresi, senza alcuna limitazione, la liceità, il
                diritto di proprietà, la convenienza o l’adeguatezza a particolari scopi o usi.
              </p>
              <p>
                L’Università degli Studi di Pavia inoltre non è in alcun modo responsabile per i servizi di
                connettività utilizzati dagli utenti del sito per la fruizione dei relativi servizi. L’Università degli
                Studi di Pavia si riserva il diritto di inibire o proibire o sospendere, in qualsiasi momento e senza
                preavviso, l’accesso al presente sito ed ai relativi servizi.
              </p>
            </Clause>

            <Clause number="3." title="Accesso a siti esterni collegati">
              <p>
                L’esistenza di un collegamento ipertestuale (link) verso un altro sito non comporta alcuna
                approvazione, neanche parziale e/o indiretta, da parte dell’Università degli Studi di Pavia circa il
                contenuto o l’utilizzazione del sito esterno. Il sito esterno è completamente indipendente da
                cerebhub.eu, che non ha alcun potere di controllo sui contenuti, sulla qualità e completezza delle
                informazioni del sito in questione.
              </p>
              <p>
                L’Università Degli Studi di Pavia non può essere ritenuta in alcun modo responsabile direttamente o
                indirettamente dei contenuti, dei servizi e dei programmi contenuti nei siti internet esterni collegati
                a questo sito e del loro relativo utilizzo da parte degli utenti. Ogni responsabilità sui contenuti e
                sull’utilizzo dei siti esterni ricade direttamente sui relativi titolari e/o responsabili.
              </p>
              <p>
                Per l’eventuale presenza dell’Università Degli Studi di Pavia sui social media, si rimanda alle
                relative condizioni di servizio in materia di copyright, che gli utenti sono tenuti a conoscere e a
                verificare.
              </p>
              <p>
                L’Università degli Studi di Pavia inoltre non è in alcun modo responsabile per i servizi di
                connettività utilizzati dagli utenti di questo sito per la connessione ai siti esterni collegati e per
                l’utilizzo dei relativi servizi.
              </p>
            </Clause>

            <Clause number="4." title="Download">
              <p>
                Gli oggetti, i programmi e i prodotti reperibili via download nel sito sono tutelati ai sensi della
                vigente normativa in materia di diritto d’autore e di proprietà industriale. Il download potrà avvenire
                per scopi non commerciali e previa verifica delle condizioni di utilizzo stabilite dai legittimi
                titolari.
              </p>
              <p>
                L’Università Degli Studi di Pavia esorta gli utenti a verificare sempre i diritti e le condizioni di
                utilizzo dei programmi e dei prodotti reperibili via download e si ritiene esplicitamente sollevata da
                qualsiasi responsabilità in merito.
              </p>
              <p>
                L’Università degli Studi di Pavia non garantisce la qualità, la sicurezza e la compatibilità dei
                programmi e dei prodotti reperibili via download con i sistemi in uso e si ritiene esente da qualsiasi
                responsabilità in merito.
              </p>
            </Clause>

            <Clause number="5." title="Virus Informatici">
              <p>
                L’Università Degli Studi di Pavia non garantisce che il sito sia privo di errori o virus. L’Università
                Degli Studi di Pavia, i suoi fornitori e i suoi collaboratori non sono responsabili per i danni subiti
                dall’utente a causa di tali elementi di carattere distruttivo.
              </p>
            </Clause>
          </div>
        </PageBody>
      </PageSurface>

      <Footer />
    </div>
  );
};

export default Legal;
